/**
 * UserMenu
 *
 * Top-right header dropdown showing the logged-in user's identity and quick
 * actions. Mirrors the B2B pattern used in business-web
 * ([apps/business-web/src/components/shared/UserMenu.tsx](../../../../../business-web/src/components/shared/UserMenu.tsx))
 * so users switching between portals get consistent UX.
 *
 * Trigger: avatar + name (name hidden on mobile).
 * Dropdown: name/email header + quick actions:
 *   - Profile      → /settings?tab=profile
 *   - Preferences  → /settings?tab=preferences
 *   - Billing      → /billing
 *   - How Tonnie works → the story of the move, in a dialog, at any time
 *   - Log out      → useLogout() → Clerk signOut + redirect to /login
 *
 * Unlike business-web this menu has no role badge or org name — journey-web
 * users are citizens, not HR admins, so that context doesn't apply.
 */

import { useTranslation } from '@tonnie/locale';
import { useState } from 'react';
import { useAuth, useUserProfile } from '@tonnie/sdk';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  UserAvatar,
} from '@tonnie/ui';
import { Bell, CreditCard, LogOut, Map as MapIcon, UserCircle } from 'lucide-react';
import { Link } from 'wouter';

import { useLogout } from '@/core/hooks/use-logout';
import { HowTonnieWorksDialog } from '@/features/platform/home/components/HowTonnieWorksDialog';
import { resolveProfileAvatarUrl } from '@/lib/avatar';

export function UserMenu() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  const { logout, isLoggingOut } = useLogout({ redirectTo: '/login' });
  const [howOpen, setHowOpen] = useState(false);

  if (!user) return null;

  const avatarUrl = resolveProfileAvatarUrl(profile, user.imageUrl);
  const fullName =
    user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email;

  // Surface the role as a subtle badge when it's a known non-citizen role.
  // Citizens are the default and don't need a visual marker.
  const role = user.role;
  const showRoleBadge = role && role !== 'citizen' && role !== 'talent';
  const roleLabel = showRoleBadge ? role.replace(/_/g, ' ') : '';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* The trigger is named by what it shows — the person's name (screen-reader-only on
              small screens) — so its accessible name contains its visible text
              (WCAG 2.5.3). The avatar's image is decorative here: the name follows. */}
          <Button variant="ghost" className="h-9 gap-2 px-2 hover:bg-muted">
            <UserAvatar src={avatarUrl} name={fullName} alt="" size="sm" />
            <span className="sr-only md:not-sr-only md:inline text-sm font-medium max-w-[140px] truncate">
              {fullName}
            </span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="font-normal">
            <div className="flex items-start gap-3 py-1">
              <UserAvatar src={avatarUrl} name={fullName} alt={fullName} size="md" />
              <div className="flex flex-col min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight truncate">{fullName}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                {showRoleBadge && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <Badge
                      variant="outline"
                      className="h-5 text-2xs bg-tone-blue-subtle text-tone-blue border-tone-blue-line capitalize"
                    >
                      {roleLabel}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <Link href="/settings?tab=profile" className="flex items-center gap-2 cursor-pointer">
              <UserCircle className="h-4 w-4" />
              <span>{t('nav.userMenu.profile')}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link
              href="/settings?tab=preferences"
              className="flex items-center gap-2 cursor-pointer"
            >
              <Bell className="h-4 w-4" />
              <span>{t('nav.userMenu.preferences')}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link href="/billing" className="flex items-center gap-2 cursor-pointer">
              <CreditCard className="h-4 w-4" />
              <span>{t('nav.userMenu.billing')}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() => setHowOpen(true)}
            className="flex items-center gap-2 cursor-pointer"
            data-testid="user-menu-how-tonnie-works"
          >
            <MapIcon className="h-4 w-4" />
            <span>{t('nav.userMenu.howItWorks')}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => logout()}
            disabled={isLoggingOut}
            className="flex items-center gap-2 cursor-pointer text-destructive-accessible focus:text-destructive-accessible focus:bg-destructive/10"
          >
            <LogOut className="h-4 w-4" />
            <span>{isLoggingOut ? t('nav.userMenu.loggingOut') : t('nav.userMenu.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <HowTonnieWorksDialog open={howOpen} onOpenChange={setHowOpen} />
    </>
  );
}
